/**
 * Page de paramètres client - Design mobile-first
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Container,
  Box,
  Typography,
  Paper,
  IconButton,
  Switch,
  FormControlLabel,
  Button,
  TextField,
  Divider,
  Alert,
  CircularProgress,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack,
  VolumeUp,
  VolumeOff,
  Lock,
  ExitToApp,
  Visibility,
  VisibilityOff,
  CheckCircle,
  Error as ErrorIcon,
  Wifi,
  DeleteOutline,
  Warning,
  PersonAdd,
  ChevronRight,
  People,
} from '@mui/icons-material';
import { logout } from '../../redux/slices/clientAuthSlice';
import { AppDispatch } from '../../redux/store';
import { clientAuthApi } from '../../services/clientApi';
import ReconnectAnefModal from '../../components/client/ReconnectAnefModal';

export default function ClientSettings() {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  // État pour la synthèse vocale
  const [voiceEnabled, setVoiceEnabled] = useState(() => {
    return localStorage.getItem('voice_enabled') === 'true';
  });

  // État pour le changement de mot de passe
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  // État pour la reconnexion ANEF
  const [showReconnectModal, setShowReconnectModal] = useState(false);

  // État pour la suppression du compte
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [showDeletePassword, setShowDeletePassword] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const handleVoiceToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    const enabled = event.target.checked;
    setVoiceEnabled(enabled);
    localStorage.setItem('voice_enabled', enabled.toString());
  };

  const handleChangePassword = async () => {
    setPasswordError('');
    setPasswordSuccess(false);

    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('Veuillez remplir tous les champs');
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError('Le nouveau mot de passe doit contenir au moins 8 caractères');
      return;
    }

    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(newPassword)) {
      setPasswordError('Le mot de passe doit contenir au moins une majuscule, une minuscule et un chiffre');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('Les mots de passe ne correspondent pas');
      return;
    }

    try {
      setPasswordLoading(true);

      const response = await clientAuthApi.changePassword(currentPassword, newPassword);

      if (response.data.success) {
        setPasswordSuccess(true);
        setTimeout(() => {
          setShowPasswordDialog(false);
          setCurrentPassword('');
          setNewPassword('');
          setConfirmPassword('');
          setPasswordSuccess(false);
        }, 2000);
      }
    } catch (error: any) {
      console.error('Erreur changement mot de passe:', error);
      setPasswordError(
        error.response?.data?.message || 'Erreur lors du changement de mot de passe'
      );
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleLogout = async () => {
    if (window.confirm('Êtes-vous sûr de vouloir vous déconnecter ?')) {
      await dispatch(logout());
      navigate('/client/login');
    }
  };

  const handleDeleteAccount = async () => {
    setDeleteError('');

    // Validation
    if (!deletePassword) {
      setDeleteError('Veuillez entrer votre mot de passe pour confirmer');
      return;
    }

    try {
      setDeleteLoading(true);

      const response = await clientAuthApi.deleteAccount(deletePassword);

      if (response.data.success) {
        // Déconnecter et rediriger
        localStorage.removeItem('clientAccessToken');
        localStorage.removeItem('clientRefreshToken');
        await dispatch(logout());
        navigate('/client/login');
      }
    } catch (error: any) {
      console.error('Erreur suppression compte:', error);
      setDeleteError(
        error.response?.data?.message || 'Erreur lors de la suppression du compte'
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pb: 4 }}>
      {/* Header fixe */}
      <Paper
        elevation={2}
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          borderRadius: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', p: 1.5, gap: 1 }}>
          <IconButton onClick={() => navigate('/client/dashboard')} size="small">
            <ArrowBack />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body1" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
              Paramètres
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Contenu */}
      <Container maxWidth="md" sx={{ mt: 2, px: 2 }}>
        {/* Préférences */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 'bold', mb: 2 }}>
            🎛️ Préférences
          </Typography>

          <List disablePadding>
            <ListItem sx={{ px: 0 }}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                {voiceEnabled ? <VolumeUp color="primary" /> : <VolumeOff />}
              </ListItemIcon>
              <ListItemText
                primary="Synthèse vocale"
                secondary="Activer les boutons d'écoute des textes"
                primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: 'medium' }}
                secondaryTypographyProps={{ fontSize: '0.75rem' }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={voiceEnabled}
                    onChange={handleVoiceToggle}
                    color="primary"
                  />
                }
                label=""
                sx={{ m: 0 }}
              />
            </ListItem>
          </List>

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="caption">
              💡 Les boutons "Écouter" apparaîtront dans les détails des dossiers si cette option est activée
            </Typography>
          </Alert>
        </Paper>

        {/* Sécurité */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 'bold', mb: 2 }}>
            🔒 Sécurité
          </Typography>

          <List disablePadding>
            <ListItem
              button
              onClick={() => setShowPasswordDialog(true)}
              sx={{
                px: 0,
                borderRadius: 1,
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <Lock color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Changer le mot de passe"
                secondary="Modifier le mot de passe de votre compte"
                primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: 'medium' }}
                secondaryTypographyProps={{ fontSize: '0.75rem' }}
              />
            </ListItem>

            <Divider sx={{ my: 1 }} />

            <ListItem
              button
              onClick={() => navigate('/client/cyber-management')}
              sx={{
                px: 0,
                borderRadius: 1,
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <PersonAdd color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Lien avec un cyber"
                secondary="Activer le suivi par un professionnel"
                primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: 'medium' }}
                secondaryTypographyProps={{ fontSize: '0.75rem' }}
              />
              <ChevronRight />
            </ListItem>

            <Divider sx={{ my: 1 }} />

            <ListItem
              button
              onClick={() => setShowReconnectModal(true)}
              sx={{
                px: 0,
                borderRadius: 1,
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <Wifi color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Reconnecter identifiants ANEF"
                secondary="Mettre à jour votre mot de passe ANEF"
                primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: 'medium' }}
                secondaryTypographyProps={{ fontSize: '0.75rem' }}
              />
            </ListItem>

            <Divider sx={{ my: 1 }} />

            <ListItem
              button
              onClick={() => navigate('/client/linked-anef-accounts')}
              sx={{
                px: 0,
                borderRadius: 1,
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <People color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Comptes ANEF rattachés"
                secondary="Suivre les dossiers d'autres personnes"
                primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: 'medium' }}
                secondaryTypographyProps={{ fontSize: '0.75rem' }}
              />
              <ChevronRight />
            </ListItem>
          </List>
        </Paper>

        {/* Déconnexion */}
        <Button
          fullWidth
          variant="outlined"
          color="error"
          startIcon={<ExitToApp />}
          onClick={handleLogout}
          sx={{ mb: 2 }}
        >
          Se déconnecter
        </Button>

        {/* Zone dangereuse */}
        <Paper sx={{ p: 2, mb: 2, borderLeft: '4px solid', borderColor: 'error.main' }}>
          <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 'bold', mb: 2, color: 'error.main' }}>
            ⚠️ Zone dangereuse
          </Typography>

          <List disablePadding>
            <ListItem
              button
              onClick={() => setShowDeleteDialog(true)}
              sx={{
                px: 0,
                borderRadius: 1,
                '&:hover': { bgcolor: 'error.lighter' },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <DeleteOutline color="error" />
              </ListItemIcon>
              <ListItemText
                primary="Supprimer mon compte"
                secondary="Cette action est irréversible et supprimera toutes vos données"
                primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: 'medium', color: 'error.main' }}
                secondaryTypographyProps={{ fontSize: '0.75rem' }}
              />
            </ListItem>
          </List>

          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="caption">
              ⚠️ La suppression de votre compte entraînera la perte définitive de tous vos dossiers, notifications et paramètres.
            </Typography>
          </Alert>
        </Paper>
      </Container>

      {/* Dialog changement de mot de passe */}
      <Dialog
        open={showPasswordDialog}
        onClose={() => !passwordLoading && setShowPasswordDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Lock color="primary" />
            <Typography variant="h6">Changer le mot de passe</Typography>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              fullWidth
              label="Mot de passe actuel"
              type={showCurrentPassword ? 'text' : 'password'}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              disabled={passwordLoading || passwordSuccess}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      edge="end"
                    >
                      {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Nouveau mot de passe"
              type={showNewPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={passwordLoading || passwordSuccess}
              helperText="Minimum 8 caractères, 1 majuscule, 1 minuscule et 1 chiffre"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      edge="end"
                    >
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Confirmer le nouveau mot de passe"
              type={showNewPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={passwordLoading || passwordSuccess}
            />

            {passwordError && (
              <Alert severity="error" icon={<ErrorIcon />}>
                {passwordError}
              </Alert>
            )}

            {passwordSuccess && (
              <Alert severity="success" icon={<CheckCircle />}>
                ✅ Mot de passe modifié avec succès !
              </Alert>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setShowPasswordDialog(false);
              setCurrentPassword('');
              setNewPassword('');
              setConfirmPassword('');
              setPasswordError('');
            }}
            disabled={passwordLoading || passwordSuccess}
          >
            Annuler
          </Button>
          <Button
            variant="contained"
            onClick={handleChangePassword}
            disabled={passwordLoading || passwordSuccess}
            startIcon={passwordLoading ? <CircularProgress size={20} /> : null}
          >
            {passwordLoading ? 'Modification...' : 'Modifier'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal reconnexion ANEF */}
      <ReconnectAnefModal
        open={showReconnectModal}
        onClose={() => setShowReconnectModal(false)}
        onSuccess={() => {
          setShowReconnectModal(false);
        }}
      />

      {/* Dialog suppression de compte */}
      <Dialog
        open={showDeleteDialog}
        onClose={() => !deleteLoading && setShowDeleteDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning color="error" />
            <Typography variant="h6" color="error.main">
              Supprimer définitivement mon compte
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Alert severity="error" icon={<Warning />}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                ⚠️ ATTENTION : Cette action est irréversible !
              </Typography>
              <Typography variant="caption" component="div" sx={{ mb: 0.5 }}>
                • Tous vos dossiers ANEF seront supprimés
              </Typography>
              <Typography variant="caption" component="div" sx={{ mb: 0.5 }}>
                • Toutes vos notifications seront perdues
              </Typography>
              <Typography variant="caption" component="div" sx={{ mb: 0.5 }}>
                • Votre abonnement sera annulé
              </Typography>
              <Typography variant="caption" component="div">
                • Vos paramètres et préférences seront effacés
              </Typography>
            </Alert>

            <Typography variant="body2">
              Pour confirmer la suppression de votre compte, veuillez entrer votre mot de passe :
            </Typography>

            <TextField
              fullWidth
              label="Mot de passe"
              type={showDeletePassword ? 'text' : 'password'}
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)}
              disabled={deleteLoading}
              autoFocus
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowDeletePassword(!showDeletePassword)}
                      edge="end"
                    >
                      {showDeletePassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {deleteError && (
              <Alert severity="error" icon={<ErrorIcon />}>
                {deleteError}
              </Alert>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setShowDeleteDialog(false);
              setDeletePassword('');
              setDeleteError('');
            }}
            disabled={deleteLoading}
          >
            Annuler
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteAccount}
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={20} /> : <DeleteOutline />}
          >
            {deleteLoading ? 'Suppression...' : 'Supprimer définitivement'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
